import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import MoyasarForm from '../components/MoyasarForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Check, Loader2, Shield, Star, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const planIcon = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('pro')) return Crown;
    if (n.includes('premium')) return Star;
    return Sparkles;
};

const SubscriptionPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { user, api } = useAuth();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [currentSub, setCurrentSub] = useState(null);
    const [preparingPlanId, setPreparingPlanId] = useState(null);
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [showPayment, setShowPayment] = useState(false);
    const [activePlan, setActivePlan] = useState(null);
    const [couponInput, setCouponInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [applyingCoupon, setApplyingCoupon] = useState(false);

    const COUPON_ERRORS = {
        coupon_invalid: { ar: 'كود الخصم غير صحيح أو غير مُفعّل.', en: 'Invalid or inactive coupon.' },
        coupon_expired: { ar: 'انتهت صلاحية كود الخصم.', en: 'This coupon has expired.' },
        coupon_exhausted: { ar: 'تم استنفاد هذا الكود.', en: 'Coupon usage limit reached.' },
        coupon_used_by_user: { ar: 'لقد استخدمت هذا الكود من قبل.', en: 'You already used this coupon.' },
    };
    const couponErrorMsg = (detail) => {
        const entry = COUPON_ERRORS[detail];
        if (!entry) return null;
        return isRTL ? entry.ar : entry.en;
    };

    useEffect(() => {
        const load = async () => {
            try {
                const accountType = user?.account_type || 'individual';
                const [plansRes, subRes] = await Promise.all([
                    api.get(`/plans?account_type=${accountType}`),
                    api.get('/subscription/me'),
                ]);
                setPlans(plansRes.data || []);
                setCurrentSub(subRes.data?.subscription || null);
            } catch (e) {
                toast.error(t('errors.somethingWentWrong') || 'Error loading plans');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [api, user, t]);

    const createPayment = async (plan, couponCode) => {
        const payload = { plan_id: plan.id, billing_cycle: billingCycle };
        if (couponCode) payload.coupon_code = couponCode;
        const res = await api.post('/payments/create', payload);

        if (res.data.free) {
            toast.success(t('subscription.freeActivated'));
            const subRes = await api.get('/subscription/me');
            setCurrentSub(subRes.data?.subscription || null);
            setShowPayment(false);
            setAppliedCoupon(null);
            setCouponInput('');
            return true;
        }

        const callbackUrl = `${window.location.origin}/payment/result?transaction_id=${res.data.transaction_id}`;
        setPaymentConfig({
            publishable_key: res.data.publishable_key,
            amount: res.data.amount,
            currency: res.data.currency,
            description: res.data.description,
            metadata: res.data.metadata,
            callback_url: callbackUrl,
        });
        setAppliedCoupon(res.data.coupon || null);
        return false;
    };

    const handleSubscribe = async (plan) => {
        setPreparingPlanId(plan.id);
        try {
            setActivePlan(plan);
            setAppliedCoupon(null);
            setCouponInput('');
            const activated = await createPayment(plan, null);
            if (!activated) setShowPayment(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('errors.somethingWentWrong') || 'Error');
        } finally {
            setPreparingPlanId(null);
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponInput.trim() || !activePlan) return;
        setApplyingCoupon(true);
        try {
            await createPayment(activePlan, couponInput.trim());
        } catch (e) {
            const msg = couponErrorMsg(e.response?.data?.detail) || e.response?.data?.detail || (isRTL ? 'تعذر تطبيق الكوبون' : 'Failed to apply coupon');
            toast.error(msg);
        } finally {
            setApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = async () => {
        if (!activePlan) return;
        setApplyingCoupon(true);
        try {
            await createPayment(activePlan, null);
            setCouponInput('');
        } catch (e) {
            toast.error(t('errors.somethingWentWrong') || 'Error');
        } finally {
            setApplyingCoupon(false);
        }
    };

    // Resolve display pricing for a plan based on its pricing mode
    const getPricing = (plan) => {
        const mode = plan.pricing_mode || 'subscription';
        if (mode === 'free') {
            return { isFree: true, amount: 0, unitLabel: '' };
        }
        if (mode === 'pay_per_use') {
            const reports = plan.limits?.reports || 1;
            const unit = reports > 1
                ? `/ ${reports} ${t('subscription.reportsUnit')}`
                : `/ ${t('subscription.reportUnit')}`;
            return { isFree: false, amount: plan.price_per_report || 0, unitLabel: unit };
        }
        const amount = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
        const unit = billingCycle === 'yearly' ? t('subscription.perYear') : t('subscription.perMonth');
        return { isFree: !amount || amount <= 0, amount, unitLabel: unit };
    };

    // Only show the monthly/yearly toggle when at least one subscription plan exists
    const hasSubscriptionPlans = plans.some((p) => (p.pricing_mode || 'subscription') === 'subscription');

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-teal-500" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="subscription-page">
            {/* Header */}
            <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-bold text-stone-800 mb-3">
                    {t('subscription.title')}
                </h1>
                <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
                    {t('subscription.subtitle')}
                </p>
            </div>

            {/* Billing toggle - only for subscription-based plans */}
            {hasSubscriptionPlans && (
            <div className="flex justify-center mb-10">
                <div className="inline-flex items-center bg-stone-100 rounded-full p-1" data-testid="billing-toggle">
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        data-testid="billing-monthly-btn"
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                            billingCycle === 'monthly'
                                ? 'bg-white text-teal-600 shadow'
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        {t('subscription.monthly')}
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        data-testid="billing-yearly-btn"
                        className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                            billingCycle === 'yearly'
                                ? 'bg-white text-teal-600 shadow'
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        {t('subscription.yearly')}
                        <span className="ms-2 text-xs text-teal-600 font-semibold">
                            {isRTL ? 'وفّر' : 'Save'}
                        </span>
                    </button>
                </div>
            </div>
            )}

            {/* Plans grid */}
            {plans.length === 0 ? (
                <p className="text-center text-stone-500" data-testid="no-plans">
                    {t('subscription.noPlans')}
                </p>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {plans.map((plan) => {
                        const Icon = planIcon(plan.name);
                        const pricing = getPricing(plan);
                        const isCurrent = currentSub?.plan_id === plan.id && currentSub?.status === 'active';
                        const isFree = pricing.isFree;
                        return (
                            <div
                                key={plan.id}
                                data-testid={`plan-card-${plan.id}`}
                                className={`relative rounded-3xl border bg-white p-8 flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${
                                    plan.is_featured
                                        ? 'border-teal-500 shadow-lg shadow-teal-500/10'
                                        : 'border-stone-200'
                                }`}
                            >
                                {plan.is_featured && (
                                    <span className="absolute -top-3 start-8 bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                        {t('subscription.mostPopular')}
                                    </span>
                                )}

                                <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center mb-5">
                                    <Icon className="w-6 h-6 text-teal-600" />
                                </div>

                                <h3 className="text-xl font-bold text-stone-800">{plan.name}</h3>
                                <p className="text-sm text-stone-500 mt-1 mb-5 min-h-[40px]">
                                    {plan.description}
                                </p>

                                <div className="mb-6">
                                    {isFree ? (
                                        <span className="text-3xl font-bold text-stone-800">
                                            {t('subscription.free')}
                                        </span>
                                    ) : (
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-bold text-stone-800">
                                                {pricing.amount}
                                            </span>
                                            <span className="text-stone-500">ر.س</span>
                                            <span className="text-sm text-stone-400 ms-1">
                                                {pricing.unitLabel}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <ul className="space-y-3 mb-8 flex-1">
                                    {(plan.features || []).map((f, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                                            <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {isCurrent ? (
                                    <Button
                                        disabled
                                        className="w-full h-12 rounded-xl bg-stone-100 text-stone-500 hover:bg-stone-100"
                                        data-testid={`current-plan-${plan.id}`}
                                    >
                                        {t('subscription.currentPlan')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={preparingPlanId === plan.id}
                                        className="w-full h-12 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-medium transition-all"
                                        data-testid={`subscribe-btn-${plan.id}`}
                                    >
                                        {preparingPlanId === plan.id ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin ltr:mr-2 rtl:ml-2" />
                                                {t('subscription.processing')}
                                            </>
                                        ) : isFree ? (
                                            t('subscription.subscribe')
                                        ) : (
                                            t('subscription.subscribe')
                                        )}
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Payment dialog */}
            <Dialog open={showPayment} onOpenChange={(o) => { setShowPayment(o); if (!o) { setAppliedCoupon(null); setCouponInput(''); } }}>
                <DialogContent className="sm:max-w-lg" data-testid="payment-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-teal-600" />
                            {t('subscription.paymentTitle')}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Coupon section */}
                    {!appliedCoupon ? (
                        <div className="flex gap-2" data-testid="coupon-section">
                            <Input
                                dir="ltr"
                                value={couponInput}
                                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                placeholder={isRTL ? 'كود الخصم (اختياري)' : 'Coupon code (optional)'}
                                className="font-mono"
                                data-testid="coupon-input"
                            />
                            <Button
                                variant="outline"
                                onClick={handleApplyCoupon}
                                disabled={applyingCoupon || !couponInput.trim()}
                                className="shrink-0 border-teal-300 text-teal-700 hover:bg-teal-50"
                                data-testid="apply-coupon-btn"
                            >
                                {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRTL ? 'تطبيق' : 'Apply')}
                            </Button>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="coupon-applied-box">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-emerald-700 font-medium">
                                    ✓ {isRTL ? 'تم تطبيق الكوبون' : 'Coupon applied'}: <span className="font-mono font-bold">{appliedCoupon.code}</span>
                                </span>
                                <button onClick={handleRemoveCoupon} disabled={applyingCoupon} className="text-stone-400 hover:text-red-500 text-xs underline" data-testid="remove-coupon-btn">
                                    {isRTL ? 'إزالة' : 'Remove'}
                                </button>
                            </div>
                            <div className="mt-1.5 text-emerald-800 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                                <span>{isRTL ? 'السعر الأصلي' : 'Original'}: {appliedCoupon.base_sar} {isRTL ? 'ر.س' : 'SAR'}</span>
                                <span>{isRTL ? 'الخصم' : 'Discount'}: -{appliedCoupon.discount_sar} {isRTL ? 'ر.س' : 'SAR'}</span>
                                <span className="font-bold">{isRTL ? 'الإجمالي' : 'Total'}: {appliedCoupon.effective_sar} {isRTL ? 'ر.س' : 'SAR'}</span>
                            </div>
                            {appliedCoupon.min_floor_applied && (
                                <p className="text-[11px] text-emerald-600 mt-1">{isRTL ? 'تم تطبيق الحد الأدنى للدفع (1 ر.س)' : 'Minimum payable amount applied (1 SAR)'}</p>
                            )}
                        </div>
                    )}

                    {paymentConfig && <MoyasarForm key={paymentConfig.metadata?.transaction_id} config={paymentConfig} />}
                    <p className="text-xs text-center text-stone-400 mt-2">
                        {t('subscription.securePayment')}
                    </p>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SubscriptionPage;
